import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { useDocumentActions } from '../../../components/documents/useDocumentActions';
import {
  useAddClosingPackageItemMutation,
  useExcludeClosingPackageItemMutation,
  useInitializeClosingPackageCompositionMutation,
  useReplaceClosingPackageItemMutation,
  useRestoreClosingPackageItemMutation,
} from '../api/closingPackageApi';
import { useDocumentSelectionHost } from './useDocumentSelectionHost';
import type { CompositionEditorRow } from '../lib/compositionEditorAdapter';
import {
  createCompositionActionRegistry,
  executeCompositionRowAction,
  executeManualAddDocument,
  executeUploadedDocumentAdd,
  type CompositionActionExecutionContext,
  type UploadedDocumentRef,
} from '../lib/compositionActionHandlers';
import { executeInitializeComposition } from '../lib/compositionInitializeHandler';
import { mapClosingPackageApiError } from '../lib/errorMapping';
import type { CompositionRowAction } from '../lib/compositionRowActions';

type PendingExcludeConfirmation = {
  context: CompositionActionExecutionContext;
  resolve: (confirmed: boolean) => void;
};

type PendingInitializeConfirmation = {
  resolve: (confirmed: boolean) => void;
};

export type CompositionExcludeConfirmModalState = {
  open: boolean;
  row: CompositionEditorRow | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export type CompositionInitializeConfirmModalState = {
  open: boolean;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const PLACEHOLDER_ACTION_MESSAGE = 'Действие будет доступно на следующем этапе';

/**
 * Wires RTK mutations + Document Selection Service into action handlers.
 * UI consumes header/row handlers and modal/picker state only.
 */
export function useCompositionActionHandlers(applicationId: number) {
  const [excludeItemMutation, { isLoading: isExcluding }] =
    useExcludeClosingPackageItemMutation();
  const [restoreItemMutation, { isLoading: isRestoring }] =
    useRestoreClosingPackageItemMutation();
  const [replaceItemMutation] = useReplaceClosingPackageItemMutation();
  const [addItemMutation] = useAddClosingPackageItemMutation();
  const [initializeCompositionMutation, { isLoading: isInitializing }] =
    useInitializeClosingPackageCompositionMutation();

  const { documentSelectionService, pickerState } = useDocumentSelectionHost();
  const {
    previewVisible,
    previewImage,
    closePreview,
    handlePreview,
    handleDownload,
  } = useDocumentActions();

  const [pendingExclude, setPendingExclude] = useState<PendingExcludeConfirmation | null>(
    null
  );
  const [pendingInitialize, setPendingInitialize] =
    useState<PendingInitializeConfirmation | null>(null);
  const [actionLoadingRowKey, setActionLoadingRowKey] = useState<string | null>(null);
  const [manualAddLoading, setManualAddLoading] = useState(false);
  const [uploadAddLoading, setUploadAddLoading] = useState(false);
  const [initializeLoading, setInitializeLoading] = useState(false);

  const requestExcludeConfirmation = useCallback(
    (context: CompositionActionExecutionContext) =>
      new Promise<boolean>((resolve) => {
        setPendingExclude({ context, resolve });
      }),
    []
  );

  const requestInitializeConfirmation = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        setPendingInitialize({ resolve });
      }),
    []
  );

  const onRowActionStart = useCallback((rowKey: string) => {
    setActionLoadingRowKey(rowKey);
  }, []);

  const onRowActionEnd = useCallback((rowKey: string) => {
    setActionLoadingRowKey((current) => (current === rowKey ? null : current));
  }, []);

  const onManualAddStart = useCallback(() => {
    setManualAddLoading(true);
  }, []);

  const onManualAddEnd = useCallback(() => {
    setManualAddLoading(false);
  }, []);

  const onUploadAddStart = useCallback(() => {
    setUploadAddLoading(true);
  }, []);

  const onUploadAddEnd = useCallback(() => {
    setUploadAddLoading(false);
  }, []);

  const onInitializeStart = useCallback(() => {
    setInitializeLoading(true);
  }, []);

  const onInitializeEnd = useCallback(() => {
    setInitializeLoading(false);
  }, []);

  const handlerDeps = useMemo(
    () => ({
      excludeItem: (args: Parameters<typeof excludeItemMutation>[0]) =>
        excludeItemMutation(args).unwrap(),
      restoreItem: (args: Parameters<typeof restoreItemMutation>[0]) =>
        restoreItemMutation(args).unwrap(),
      replaceItem: (args: Parameters<typeof replaceItemMutation>[0]) =>
        replaceItemMutation(args).unwrap(),
      addItem: (args: Parameters<typeof addItemMutation>[0]) => addItemMutation(args).unwrap(),
      previewDocument: handlePreview,
      downloadDocument: handleDownload,
      documentSelectionService,
      notifySuccess: (text: string) => message.success(text),
      notifyError: (error: unknown, fallback: string) =>
        message.error(mapClosingPackageApiError(error, fallback)),
      requestExcludeConfirmation,
      onRowActionStart,
      onRowActionEnd,
    }),
    [
      addItemMutation,
      documentSelectionService,
      handleDownload,
      handlePreview,
      excludeItemMutation,
      onRowActionEnd,
      onRowActionStart,
      replaceItemMutation,
      requestExcludeConfirmation,
      restoreItemMutation,
    ]
  );

  const registry = useMemo(
    () => createCompositionActionRegistry(handlerDeps),
    [handlerDeps]
  );

  const handleRowAction = useCallback(
    async (row: CompositionEditorRow, action: CompositionRowAction) => {
      const result = await executeCompositionRowAction(registry, action.kind, {
        applicationId,
        row,
      });

      if (result === 'unavailable') {
        message.info(`${action.label}: ${PLACEHOLDER_ACTION_MESSAGE}`);
      }
    },
    [applicationId, registry]
  );

  const handleManualAddDocument = useCallback(async (disabledDocumentIds: number[] = []) => {
    await executeManualAddDocument(
      {
        addItem: handlerDeps.addItem,
        documentSelectionService: handlerDeps.documentSelectionService,
        notifySuccess: handlerDeps.notifySuccess,
        notifyError: handlerDeps.notifyError,
        onManualAddStart,
        onManualAddEnd,
      },
      applicationId,
      disabledDocumentIds
    );
  }, [applicationId, handlerDeps, onManualAddEnd, onManualAddStart]);

  const handleUploadedDocuments = useCallback(
    async (documents: UploadedDocumentRef[]) =>
      executeUploadedDocumentAdd(
        {
          addItem: handlerDeps.addItem,
          notifySuccess: handlerDeps.notifySuccess,
          notifyError: handlerDeps.notifyError,
          notifyWarning: (text) => message.warning(text),
          onUploadAddStart,
          onUploadAddEnd,
        },
        applicationId,
        documents
      ),
    [applicationId, handlerDeps, onUploadAddEnd, onUploadAddStart]
  );

  const handleInitializeComposition = useCallback(async () => {
    await executeInitializeComposition(
      {
        initializeComposition: (args) => initializeCompositionMutation(args).unwrap(),
        requestInitializeConfirmation,
        notifySuccess: handlerDeps.notifySuccess,
        notifyError: handlerDeps.notifyError,
        onInitializeStart,
        onInitializeEnd,
      },
      applicationId
    );
  }, [
    applicationId,
    handlerDeps,
    initializeCompositionMutation,
    onInitializeEnd,
    onInitializeStart,
    requestInitializeConfirmation,
  ]);

  const confirmExclude = useCallback(() => {
    if (!pendingExclude) {
      return;
    }
    pendingExclude.resolve(true);
    setPendingExclude(null);
  }, [pendingExclude]);

  const cancelExclude = useCallback(() => {
    if (!pendingExclude) {
      return;
    }
    pendingExclude.resolve(false);
    setPendingExclude(null);
  }, [pendingExclude]);

  const confirmInitialize = useCallback(() => {
    if (!pendingInitialize) {
      return;
    }
    pendingInitialize.resolve(true);
    setPendingInitialize(null);
  }, [pendingInitialize]);

  const cancelInitialize = useCallback(() => {
    if (!pendingInitialize) {
      return;
    }
    pendingInitialize.resolve(false);
    setPendingInitialize(null);
  }, [pendingInitialize]);

  const excludeConfirmModal: CompositionExcludeConfirmModalState = {
    open: pendingExclude != null,
    row: pendingExclude?.context.row ?? null,
    loading: isExcluding || isRestoring,
    onConfirm: confirmExclude,
    onCancel: cancelExclude,
  };

  const initializeConfirmModal: CompositionInitializeConfirmModalState = {
    open: pendingInitialize != null,
    loading: initializeLoading || isInitializing,
    onConfirm: confirmInitialize,
    onCancel: cancelInitialize,
  };

  return {
    handleRowAction,
    handleManualAddDocument,
    handleUploadedDocuments,
    handleInitializeComposition,
    excludeConfirmModal,
    initializeConfirmModal,
    documentPickerState: pickerState,
    documentPreviewState: {
      open: previewVisible,
      imageUrl: previewImage,
      onClose: closePreview,
    },
    actionLoadingRowKey,
    manualAddLoading,
    uploadAddLoading,
    initializeLoading: initializeLoading || isInitializing,
  };
}

export type UseCompositionActionHandlersResult = ReturnType<
  typeof useCompositionActionHandlers
>;
